import {
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Edit,
  EyeOff,
  GripVertical,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableCell, TableRow } from "@/components/ui/table";
import type { ServiceCategory } from "@/gql/graphql";
import { cn } from "@/lib/utils";
import {
  useArchiveServiceCategory,
  useRestoreServiceCategory,
} from "../../hooks/use-services-mutation";

interface CategoryRowProps {
  category: ServiceCategory;
  serviceCount: number;
  salonId: string;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onEditCategory: (category: ServiceCategory) => void;
  onCreateService: (categoryId: string) => void;
  isDragInProgress?: boolean;
  isUpdating?: boolean;
  isDragOverlay?: boolean;
  hasActiveServices?: boolean;
  isSearching?: boolean;
  isEditMode?: boolean;
  dragListeners?: any;
}

export function CategoryRow({
  category,
  serviceCount: _serviceCount,
  salonId,
  isExpanded,
  onToggleExpanded,
  onEditCategory,
  onCreateService: _onCreateService,
  isDragInProgress: _isDragInProgress = false,
  isUpdating = false,
  isDragOverlay = false,
  hasActiveServices: _hasActiveServices = false,
  isSearching = false,
  isEditMode = false,
  dragListeners = {},
}: CategoryRowProps) {
  const { t } = useTranslation("services");
  // Note: Drag and drop is now handled by the parent TableBody in SortableCategorySection

  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);

  // Domain hooks for mutations
  const { execute: archiveCategoryAction, isLoading: archiveCategoryLoading } =
    useArchiveServiceCategory();
  const { execute: restoreCategoryAction, isLoading: restoreCategoryLoading } =
    useRestoreServiceCategory();

  const handleArchiveCategory = async () => {
    const result = await archiveCategoryAction(salonId, category.id);
    if (result) {
      setArchiveDialogOpen(false);
    }
  };

  const handleRestoreCategory = async () => {
    const result = await restoreCategoryAction(salonId, category.id);
    if (result) {
      setRestoreDialogOpen(false);
    }
  };

  return (
    <>
      <TableRow
        className={cn(
          "bg-background hover:bg-muted/50 border-b-2 border-border",
          isDragOverlay && "shadow-lg bg-background border-primary/30"
        )}
      >
        {/* Desktop Drag Handle - only on desktop when not searching */}
        {!isSearching && !isEditMode && (
          <TableCell className="w-8 p-2 hidden sm:table-cell">
            <div
              {...dragListeners}
              className={cn(
                "p-1 rounded transition-opacity flex items-center justify-center",
                isUpdating
                  ? "cursor-not-allowed opacity-30"
                  : "cursor-grab active:cursor-grabbing hover:bg-muted/50 opacity-60 hover:opacity-100"
              )}
              title={isUpdating ? "Updating order..." : "Drag to reorder"}
            >
              <GripVertical
                className={cn(
                  "w-4 h-4",
                  isUpdating ? "text-muted-foreground/50" : "text-muted-foreground"
                )}
              />
            </div>
          </TableCell>
        )}
        {/* Mobile Edit Mode Drag Handle */}
        {isEditMode && (
          <TableCell className="w-12 p-2 sm:hidden">
            <div
              {...dragListeners}
              className={cn(
                "h-10 w-10 rounded transition-opacity flex items-center justify-center",
                isUpdating
                  ? "cursor-not-allowed opacity-30"
                  : "cursor-grab active:cursor-grabbing hover:bg-muted/50 text-muted-foreground"
              )}
              title={isUpdating ? "Updating order..." : "Drag to reorder"}
            >
              <GripVertical
                className={cn(
                  "w-5 h-5",
                  isUpdating ? "text-muted-foreground/50" : "text-muted-foreground"
                )}
              />
            </div>
          </TableCell>
        )}

        {/* Category Info */}
        <TableCell
          className="font-medium cursor-pointer py-3 sm:py-2"
          onClick={isDragOverlay ? undefined : onToggleExpanded}
        >
          <div className="flex items-start gap-2">
            {/* Expand/Collapse Arrow - Centered between title and description */}
            <div className="flex items-center justify-center w-4 flex-shrink-0 self-stretch">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </div>

            {/* Category Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {/* Category Color Indicator */}
                <div
                  className="w-3 h-3 rounded-full border flex-shrink-0"
                  style={{ backgroundColor: category.color }}
                />

                {/* Category Name */}
                <span className="font-semibold text-lg">{category.name}</span>

                {/* Status badges */}
                <div className="flex items-center gap-1.5 ml-2">
                  {!category.allowOnlineBooking && (
                    <Badge
                      variant="outline"
                      className="text-[13px] sm:text-xs flex items-center gap-1"
                    >
                      <EyeOff className="w-3.5 h-3.5 sm:w-3 sm:h-3" />
                      Private
                    </Badge>
                  )}
                  {category.isArchived && (
                    <Badge variant="destructive" className="text-[13px] sm:text-xs">
                      Archived
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </TableCell>

        {/* Desktop only columns - empty cells */}
        <TableCell className="hidden sm:table-cell w-40" />
        <TableCell className="hidden sm:table-cell w-28" />
        <TableCell className="hidden sm:table-cell w-32" />

        {/* Actions - Hide in mobile edit mode */}
        {!isEditMode && (
          <TableCell className="w-12 sm:w-32 text-right p-2">
            <div className="flex items-center justify-end gap-1">
              {/* Desktop Actions */}
              <div className="hidden sm:flex items-center gap-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => e.stopPropagation()}
                      className="h-8 w-8 p-0"
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEditCategory(category)}>
                      <Edit className="w-4 h-4 mr-2" />
                      {t("actions.editCategory")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {category.isArchived ? (
                      <DropdownMenuItem
                        onClick={() => setRestoreDialogOpen(true)}
                        className="text-green-600"
                      >
                        <ArrowUp className="w-4 h-4 mr-2" />
                        {t("actions.restoreCategory")}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={() => setArchiveDialogOpen(true)}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t("actions.archiveCategory")}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Mobile Actions */}
              <div className="sm:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => e.stopPropagation()}
                      className="h-10 w-10 p-0"
                    >
                      <MoreHorizontal className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEditCategory(category)}>
                      <Edit className="w-4 h-4 mr-2" />
                      {t("actions.editCategory")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {category.isArchived ? (
                      <DropdownMenuItem
                        onClick={() => setRestoreDialogOpen(true)}
                        className="text-green-600"
                      >
                        <ArrowUp className="w-4 h-4 mr-2" />
                        {t("actions.restoreCategory")}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={() => setArchiveDialogOpen(true)}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t("actions.archiveCategory")}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </TableCell>
        )}
        {/* Mobile edit mode - Archive/Restore button */}
        {isEditMode && (
          <TableCell className="w-12 text-right sm:hidden p-2">
            {category.isArchived ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setRestoreDialogOpen(true);
                }}
                className="h-10 w-10 p-0 text-green-600 hover:text-green-700"
              >
                <ArrowUp className="w-5 h-5" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setArchiveDialogOpen(true);
                }}
                className="h-10 w-10 p-0 text-destructive hover:text-destructive/80"
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            )}
          </TableCell>
        )}
      </TableRow>

      {/* Archive Category Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm.archiveCategory.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirm.archiveCategory.description", { name: category.name })}
              <span className="block mt-2">{t("confirm.archiveCategory.detail")}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveCategory}
              disabled={archiveCategoryLoading}
              className="bg-destructive hover:bg-destructive/90"
            >
              {archiveCategoryLoading
                ? t("confirm.archiveCategory.loading")
                : t("confirm.archiveCategory.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Category Dialog */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm.restoreCategory.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirm.restoreCategory.description", { name: category.name })}
              <span className="block mt-2">{t("confirm.restoreCategory.detail")}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestoreCategory}
              disabled={restoreCategoryLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {restoreCategoryLoading
                ? t("confirm.restoreCategory.loading")
                : t("confirm.restoreCategory.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
